import "@pankod/refine-antd/dist/styles.min.css";
import { ResourceProps } from "@pankod/refine-core";

import { UserRole } from "enums";
import { AuditLogList } from "pages/audit-log";
import { USER_DATA } from "environment";

import {
  BranchOfficesCreate,
  BranchOfficesEdit,
  BranchOfficesList,
  BranchOfficesShow,
} from "pages/branch-offices";
import {
  BranchMunicipalitiesCreate,
  BranchMunicipalitiesEdit,
  BranchMunicipalitiesList,
  BranchMunicipalitiesShow,
} from "pages/branches-municipalities";
import { BudgetsList, BudgetsShow } from "pages/budgets";
import { BudgetsIndicatedList } from "pages/budgets-indicated";
import { RedisList, RedisShow } from "pages/cache-redis";
import {
  CampaignCreate,
  CampaignEdit,
  CampaignList,
  CampaignShow,
} from "pages/campaign";
import {
  ContentsCreate,
  ContentsEdit,
  ContentsList,
  ContentsShow,
} from "pages/cms";
import { 
  CustomizableContentCreate,
  CustomizableContentEdit,
  CustomizableContentList,
  CustomizableContentShow
} from "pages/customizable-content";
import { FileList } from "pages/file-manager";
import {
  FeatureFlagsCreate,
  FeatureFlagsEdit,
  FeatureFlagsList,
  FeatureFlagsShow,
} from "pages/feature-flags";
import {
  FEATURE_FLAGS_RESOURCE,
  FEATURE_FLAGS_ROUTE,
} from "pages/feature-flags/utils/feature-flags.constants";
import {
  HealthInsurerCreate,
  HealthInsurerEdit,
  HealthInsurerList,
  HealthInsurerShow,
} from "pages/health-insurer";
import {
  HolidaysCreate,
  HolidaysEdit,
  HolidaysList,
  HolidaysShow,
} from "pages/holidays";
import { LogsNavigationList } from "pages/logs-navigation/list";
import {
  OriginsCreate,
  OriginsEdit,
  OriginsList,
  OriginsShow,
} from "pages/origins";
import { ParameterizeTriggersTemplatesEdit } from "pages/parameterize-triggers-templates";
import { ParameterizeTriggersTemplatesCreate } from "pages/parameterize-triggers-templates/create";
import { ParameterizeTriggersTemplatesList } from "pages/parameterize-triggers-templates/list";
import {
  ParametrizationsCreate,
  ParametrizationsEdit,
  ParametrizationsList,
  ParametrizationsShow,
} from "pages/parametrizations";
import {
  PartnerCreate,
  PartnerEdit,
  PartnerList,
  PartnerShow,
} from "pages/partner";
import {
  PartnerUsersCreate,
  PartnerUsersEdit,
  PartnerUsersList,
  PartnerUsersShow,
} from "pages/partner-users";
import {
  PlansFamilyCreate,
  PlansFamilyEdit,
  PlansFamilyList,
  PlansFamilyShow,
} from "pages/plans-family";
import {
  PresencePointsCreate,
  PresencePointsEdit,
  PresencePointsList,
  PresencePointsShow,
} from "pages/presence-points";
import { PlanReloadList, PlanReloadShow } from "pages/plan-reload";
import { LegacySendsList, LegacySendsShow } from "pages/legacy-sends";
import {
  SellersCreate,
  SellersEdit,
  SellersList,
  SellersShow,
} from "pages/sellers";
import { ShootBlockingCreate, ShootBlockingList } from "pages/shoot-blocking";
import {
  ShootExecutionCreate,
  ShootExecutionList,
} from "pages/shoot-execution";
import { ShootExecutionEdit } from "pages/shoot-execution/edit";
import { TriggerReportsList } from "pages/trigger-reports";
import { UsersCreate, UsersEdit, UsersList, UsersShow } from "pages/users";

const adminSettingsResources: ResourceProps[] = [
  {
    name: "settings",
    options: { label: "Configurações" },
  },
  {
    name: "v1/health-insurer",
    parentName: "settings",
    options: { label: "Operadoras", route: "health-insurer" },
    list: HealthInsurerList,
    create: HealthInsurerCreate,
    edit: HealthInsurerEdit,
    show: HealthInsurerShow,
  },
  {
    name: "v1/partners",
    parentName: "settings",
    options: { label: "Parceiros", route: "partners" },
    list: PartnerList,
    create: PartnerCreate,
    edit: PartnerEdit,
    show: PartnerShow,
  },
  {
    name: "v1/partner-users",
    parentName: "settings",
    options: { label: "Usuários Parceiros", route: "partner-users" },
    list: PartnerUsersList,
    create: PartnerUsersCreate,
    edit: PartnerUsersEdit,
    show: PartnerUsersShow,
    canDelete: true,
  },
  {
    name: "v1/campaign",
    parentName: "settings",
    options: { label: "Campanhas", route: "campaign" },
    list: CampaignList,
    create: CampaignCreate,
    edit: CampaignEdit,
    show: CampaignShow,
  },
  {
    name: "v1/origin",
    parentName: "settings",
    options: { label: "Origens", route: "origins" },
    list: OriginsList,
    create: OriginsCreate,
    edit: OriginsEdit,
    show: OriginsShow,
  },
  {
    name: FEATURE_FLAGS_RESOURCE,
    parentName: "settings",
    options: {
      label: "Controle de Funcionalidades",
      route: FEATURE_FLAGS_ROUTE,
    },
    list: FeatureFlagsList,
    create: FeatureFlagsCreate,
    edit: FeatureFlagsEdit,
    show: FeatureFlagsShow,
  },
  {
    name: "v1/plan-family",
    parentName: "settings",
    options: { label: "Família de Planos", route: "plans-family" },
    list: PlansFamilyList,
    create: PlansFamilyCreate,
    edit: PlansFamilyEdit,
    show: PlansFamilyShow,
  },
  {
    name: "v1/branch-office",
    parentName: "settings",
    options: { label: "Filiais/Coberturas", route: "branch-offices" },
    list: BranchOfficesList,
    create: BranchOfficesCreate,
    edit: BranchOfficesEdit,
    show: BranchOfficesShow,
  },
  {
    name: "v1/presence-point",
    parentName: "settings",
    options: { label: "Rede de Cobertura", route: "presence-point" },
    list: PresencePointsList,
    create: PresencePointsCreate,
    edit: PresencePointsEdit,
    show: PresencePointsShow,
  },
  {
    name: "v1/seller",
    parentName: "settings",
    options: { label: "Vendedores On-line", route: "sellers" },
    list: SellersList,
    create: SellersCreate,
    edit: SellersEdit,
    show: SellersShow,
  },
  {
    name: "v1/parametrization",
    parentName: "settings",
    options: { label: "Parametrizações", route: "parametrizations" },
    list: ParametrizationsList,
    create: ParametrizationsCreate,
    edit: ParametrizationsEdit,
    show: ParametrizationsShow,
  },
  {
    name: "v1/parametrizable-content/sites",
    parentName: "settings",
    options: { label: "Conteúdo Parametrizável", route: "customizable-content" },
    list: CustomizableContentList,
    create: CustomizableContentCreate,
    edit: CustomizableContentEdit,
    show: CustomizableContentShow,
  },
  {
    name: "v1/cms",
    parentName: "settings",
    options: { label: "Gestão de Conteúdo", route: "cms" },
    list: ContentsList,
    create: ContentsCreate,
    edit: ContentsEdit,
    show: ContentsShow,
  },
  {
    name: "v1/cms-files",
    parentName: "settings",
    options: { label: "Porta Arquivos", route: "files" },
    list: FileList,
  },
  {
    name: "v1/holidays",
    parentName: "settings",
    options: { label: "Feriados", route: "holidays" },
    list: HolidaysList,
    create: HolidaysCreate,
    edit: HolidaysEdit,
    show: HolidaysShow,
  },
];

const reportResources: ResourceProps[] = [
  {
    name: "reports",
    options: { label: "Relatórios" },
  },
  {
    name: "v1/budget",
    parentName: "reports",
    options: { label: "Orçamentos", route: "budgets" },
    list: BudgetsList,
    show: BudgetsShow,
  },
  {
    name: "v1/indications",
    parentName: "reports",
    options: { label: "Indicações", route: "budgets-indicated" },
    list: BudgetsIndicatedList,
  },
  {
    // Spec 039 — acompanhamento do Legacy.Consumer. Fica em `reportResources`, e NÃO em
    // `systemAdminResources`, de propósito: aquele grupo só é montado para `UserRole.Admin`, e o
    // público desta tela é operação/atendimento (RN10). O que é sensível — reprocessar, `run_id`,
    // `http_status` — é escondido dentro da tela por `usePermissions`, e o endpoint recusa a
    // chamada de quem não é Admin.
    name: "v1/legacy-consumer-log",
    parentName: "reports",
    options: { label: "Envios ao Legado", route: "legacy-sends" },
    list: LegacySendsList,
    show: LegacySendsShow,
  },
];

const engagingResources: ResourceProps[] = [
  {
    name: "engaging",
    options: { label: "Engajamento" },
  },
  {
    name: "v1/repique-campaign",
    parentName: "engaging",
    options: {
      label: "Parametrizar Disparos x Templates",
      route: "parameterize-triggers-templates",
    },
    list: ParameterizeTriggersTemplatesList,
    create: ParameterizeTriggersTemplatesCreate,
    edit: ParameterizeTriggersTemplatesEdit,
  },
  {
    name: "v1/campaign-executions",
    parentName: "engaging",
    options: { label: "Executar Disparo", route: "campaign-executions" },
    list: ShootExecutionList,
    create: ShootExecutionCreate,
    edit: ShootExecutionEdit,
  },
  {
    name: "v1/shoot-blocking",
    parentName: "engaging",
    options: { label: "Bloqueio de Disparos", route: "shoot-blocking" },
    list: ShootBlockingList,
    create: ShootBlockingCreate,
  },
  {
    name: "v1/trigger-reports",
    parentName: "engaging",
    options: { label: "Relatórios de Disparos", route: "trigger-reports" },
    list: TriggerReportsList,
  },
];

const systemAdminResources: ResourceProps[] = [
  {
    name: "systemAdmin",
    options: { label: "Gestão do Sistema" },
  },
  {
    name: "v1/users",
    parentName: "systemAdmin",
    options: { label: "Usuários", route: "users" },
    list: UsersList,
    create: UsersCreate,
    edit: UsersEdit,
    show: UsersShow,
  },
  {
    name: "v1/redis",
    parentName: "systemAdmin",
    options: { label: "Gestão de Cache", route: "redis" },
    list: RedisList,
    show: RedisShow,
  },
  {
    name: "v1/audit-log",
    parentName: "systemAdmin",
    options: { label: "Auditoria", route: "audit-log" },
    list: AuditLogList,
  },
  {
    name: "v1/navigation-log",
    parentName: "systemAdmin",
    options: { label: "Logs de navegação", route: "navigation-log" },
    list: LogsNavigationList,
  },
  {
    // Tela unificada (spec 027): disparo de reprocessamento + dashboard de acompanhamento.
    // Aposenta o antigo "Painel de Controle" (v1/xpto). Admin-only (systemAdminResources).
    name: "v1/consumer-log",
    parentName: "systemAdmin",
    options: { label: "Recarga de Planos", route: "plan-reload" },
    list: PlanReloadList,
    show: PlanReloadShow,
  },
  {
    name: "v1/branch-office/linked-places",
    parentName: "systemAdmin",
    options: { label: "Filiais/Municípios", route: "municipalities" },
    list: BranchMunicipalitiesList,
    create: BranchMunicipalitiesCreate,
    edit: BranchMunicipalitiesEdit,
    show: BranchMunicipalitiesShow,
  },
];

export const resourcesProvider = (): ResourceProps[] => {
  const userdata = localStorage.getItem(USER_DATA);
  const role = userdata
    ? JSON.parse(userdata).roles ?? UserRole.ReportsBasic
    : "";

  if (!role) return [{ name: "" }];

  let resourcesList: ResourceProps[] = [];

  if (role === UserRole.Admin) {
    resourcesList = [
      ...adminSettingsResources,
      ...reportResources,
      ...engagingResources,
      ...systemAdminResources,
    ];
  } else if (role === UserRole.ReportsFull || role === UserRole.ReportsBasic) {
    resourcesList = [...reportResources, ...engagingResources];
  }

  return resourcesList.length ? resourcesList : [{ name: "" }];
};
